import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { AnyAction, Reducer } from "redux";
import { IAppDispatch, IAppThunkAction } from ".";
import AccountApi from "../api/AccountApi";
import { updatePosthogUserInfo } from "../helpers/Posthog";
import { updateSentryUserInfo } from "../helpers/Sentry";
import IRegisterAccount from "../models/account/IRegisterAccount";
import IResetPassword, { ISetPassword } from "../models/account/IResetPassword";
import IUserInfo from "../models/account/IUserInfo";
import IUserLogin from "../models/account/IUserLogin";
import IUserProfile, {
    IUserProfileResponse
} from "../models/account/IUserProfile";
import IJwtToken from "../models/security/IJwtToken";
import { LoadState } from "../models/utility/LoadState";
import AuthCookieProvider from "../providers/AuthCookieProvider";
import SignalRProvider from "../providers/SignalRProvider";
import TokenProvider from "../providers/TokenProvider";

export interface IUserStoreState {
    admins: IUserInfo[];
    authLoadState: LoadState;
    bidders: IUserInfo[];
    captchaKey: string;
    createUserFailure: boolean;
    createUserFailErrors: string[];
    createUserState: LoadState;
    createUserSucceed: boolean;
    deleteUserState: LoadState;
    deleteUserSucceed: boolean;
    forgotPasswordFailure: boolean;
    forgotPasswordFailErrors: string[];
    forgotPasswordSucceed: boolean;
    logonFailure: boolean;
    prevLocation: string;
    redirectToLogin: boolean;
    regFailure: boolean;
    regFailErrors: string[];
    resetPasswordFailure: boolean;
    resetPasswordFailErrors: string[];
    resetPasswordTokenError: boolean;
    resetPasswordTokenLoadState: LoadState;
    setPasswordFailure: boolean;
    setPasswordFailErrors: string[];
    setPasswordLoadState: LoadState;
    setPasswordSucceed: boolean;
    stripeKey: string;
    updateUserProfileFailure: boolean;
    updateUserProfileFailErrors: string[];
    updateUserProfileSucceed: boolean;
    user: IUserLogin | null;
    usersLoadState: LoadState;
    userProfile: IUserProfile | null;
    userProfileLoadState: LoadState;
}

export type KnownAction =
    | ICreateUserLoadState
    | ICreateUser
    | ICreateUserFailure
    | IDeleteUserLoadState
    | IDeleteUser
    | IUpdateUserStatus
    | IForgotPassword
    | IForgotPasswordFailure
    | IForgotPasswordLoadState
    | IGetUserProfileLoadState
    | IGetUserProfile
    | ILoadUserInfo
    | ILogoutUser
    | IRegistrationTokensLoadState
    | ISetPasswordSuccess
    | ISetPasswordFailure
    | ISetPasswordLoadState
    | IUpdateRegistrationTokens
    | IToggleLogonFailure
    | IToggleLogonLoadState
    | IToggleRedirectToLogin
    | IToggleRegFailure
    | IToggleResetCreateUser
    | IToggleResetDeleteUser
    | IToggleResetPasswordFailure
    | IToggleResetSetPassword
    | IToggleResetTokenError
    | IToggleResetTokenLoadState
    | IToggleResetUpdateUserProfile
    | IToggleUserListLoadState
    | IUpdateLocation
    | IUpdateUserList
    | IUpdateUserProfileLoadState
    | IUpdateUserProfile
    | IUpdateUserProfileFailure;

export interface ICreateUserLoadState {
    type: "CREATE_USER_LOAD_STATE";
    createUserState: LoadState;
}
export interface ICreateUser {
    type: "CREATE_USER";
}
export interface ICreateUserFailure {
    type: "CREATE_USER_FAILURE";
    failure: boolean;
    errors: string[];
}
export interface IDeleteUserLoadState {
    type: "DELETE_USER_LOAD_STATE";
    deleteUserState: LoadState;
}
export interface IDeleteUser {
    type: "DELETE_USER";
}
export interface IUpdateUserStatus {
    type: "UPDATE_USER_STATUS";
    email: string;
    enable: boolean;
}
export interface ILoadUserInfo {
    type: "LOAD_USER_INFO";
    token: IJwtToken | null;
}
export interface IToggleLogonFailure {
    type: "TOGGLE_LOGON_FAILURE";
    failure: boolean;
}
export interface IToggleLogonLoadState {
    type: "TOGGLE_LOGON_LOAD_STATE";
    authLoadState: LoadState;
}
export interface ILogoutUser {
    type: "LOGOUT_USER";
}
export interface IRegistrationTokensLoadState {
    type: "REGISTRATION_TOKENS_INFO_LOAD_STATE";
    authLoadState: LoadState;
}
export interface IUpdateRegistrationTokens {
    type: "REGISTRATION_TOKENS_INFO";
    authLoadState: LoadState;
    captchaKey: string;
    stripeKey: string;
}
export interface IForgotPasswordLoadState {
    type: "FORGOT_PASSWORD_LOAD_STATE";
    authLoadState: LoadState;
}
export interface IForgotPassword {
    type: "FORGOT_PASSWORD";
}
export interface IForgotPasswordFailure {
    type: "FORGOT_PASSWORD_FAILURE";
    failure: boolean;
    errors: string[];
}
export interface ISetPasswordLoadState {
    type: "SET_PASSWORD_LOAD_STATE";
    setPasswordLoadState: LoadState;
}
export interface ISetPasswordSuccess {
    type: "SET_PASSWORD";
}
export interface ISetPasswordFailure {
    type: "SET_PASSWORD_FAILURE";
    failure: boolean;
    errors: string[];
}
export interface IToggleResetPasswordFailure {
    type: "TOGGLE_RESET_PASSWORD_FAILURE";
    failure: boolean;
    errors: string[];
}
export interface IToggleRegFailure {
    type: "TOGGLE_REG_FAILURE";
    failure: boolean;
    errors: string[];
}
export interface IUpdateLocation {
    type: "UPDATE_LOCATION";
    location: string;
}
export interface IToggleResetTokenLoadState {
    type: "TOGGLE_RESET_TOKEN_LOAD_STATE";
    resetPasswordTokenLoadState: LoadState;
}
export interface IToggleResetTokenError {
    type: "TOGGLE_RESET_TOKEN_ERROR";
    resetPasswordTokenError: boolean;
}
export interface IToggleUserListLoadState {
    type: "TOGGLE_USER_LIST_LOAD_STATE";
    usersLoadState: LoadState;
}
export interface IGetUserProfileLoadState {
    type: "GET_USER_PROFILE_LOAD_STATE";
    userProfileLoadState: LoadState;
}
export interface IGetUserProfile {
    type: "GET_USER_PROFILE";
    userProfile: IUserProfile | null;
}
export interface IUpdateUserList {
    type: "UPDATE_USER_LIST";
    admins: IUserInfo[];
    bidders: IUserInfo[];
    usersLoadState: LoadState;
}
export interface IUpdateUserProfileLoadState {
    type: "UPDATE_USER_PROFILE_LOAD_STATE";
    authLoadState: LoadState;
}
export interface IUpdateUserProfile {
    type: "UPDATE_USER_PROFILE";
}
export interface IUpdateUserProfileFailure {
    type: "UPDATE_USER_PROFILE_FAILURE";
    failure: boolean;
    errors: string[];
}
export interface IToggleResetUpdateUserProfile {
    type: "TOGGLE_RESET_UPDATE_USER_PROFILE";
}
export interface IToggleResetCreateUser {
    type: "TOGGLE_RESET_CREATE_USER";
}
export interface IToggleResetDeleteUser {
    type: "TOGGLE_RESET_DELETE_USER";
}
export interface IToggleResetSetPassword {
    type: "TOGGLE_RESET_SET_PASSWORD";
}
export interface IToggleRedirectToLogin {
    type: "TOGGLE_REDIRECT_TO_LOGIN";
    redirect: boolean;
}

const loadUserProfile =
    (fetchProfile: () => Promise<IUserProfileResponse>): IAppThunkAction =>
    async (dispatch) => {
        dispatch({
            type: "GET_USER_PROFILE_LOAD_STATE",
            userProfileLoadState: LoadState.Loading
        });

        let userProfile: IUserProfile | null = null;
        try {
            userProfile = (await fetchProfile()).profile ?? null;
        } catch {
            // An empty error body or a network failure still ends the load.
        }
        dispatch({ type: "GET_USER_PROFILE", userProfile });
    };

export const actionCreators = {
    createUser:
        (userProfile: IUserProfile): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "CREATE_USER_LOAD_STATE",
                createUserState: LoadState.Loading
            });

            // call API to register and get a token in return
            const result = await AccountApi.CreateUserAccount(userProfile);
            if (result.userId) {
                dispatch({ type: "CREATE_USER" });
                dispatch({ type: "TOGGLE_RESET_CREATE_USER" });
            } else {
                const errors = result.errors.map((e) => e.description);
                dispatch({
                    type: "CREATE_USER_FAILURE",
                    failure: true,
                    errors
                });
            }
        },

    deleteUser:
        (email: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "DELETE_USER_LOAD_STATE",
                deleteUserState: LoadState.Loading
            });
            const result = await AccountApi.DeleteUser(email);

            if (result) {
                // successfully deleted user
                dispatch({ type: "DELETE_USER" });
                dispatch({ type: "TOGGLE_RESET_DELETE_USER" });
            }
        },

    updateUserStatus:
        (email: string, enable: boolean): IAppThunkAction =>
        async (dispatch) => {
            const result = await AccountApi.UpdateUserStatus(email, enable);

            if (result) {
                // successfully enable or disable a user
                dispatch({ type: "UPDATE_USER_STATUS", email, enable });
            }
        },

    forgotPassword:
        (email: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "FORGOT_PASSWORD_LOAD_STATE",
                authLoadState: LoadState.Loading
            });
            // call API to attempt login
            const result = await AccountApi.ForgotPassword(email);
            if (result.errors.length === 0) {
                dispatch({ type: "FORGOT_PASSWORD" });
            } else {
                const errors = result.errors.map((e) => e.description);
                dispatch({
                    type: "FORGOT_PASSWORD_FAILURE",
                    failure: true,
                    errors
                });
            }
        },

    getMyProfile: (): IAppThunkAction =>
        loadUserProfile(() => AccountApi.GetMyProfile()),

    getUserProfile: (id: string): IAppThunkAction =>
        loadUserProfile(() => AccountApi.GetUserProfile(id)),

    loadRegistrationPublicTokens: (): IAppThunkAction => async (dispatch) => {
        dispatch({
            type: "REGISTRATION_TOKENS_INFO_LOAD_STATE",
            authLoadState: LoadState.Loading
        });

        const response = await AccountApi.GetRegistrationTokens();
        dispatch({
            authLoadState: LoadState.Loaded,
            captchaKey: response.captchaKey,
            stripeKey: response.stripeKey,
            type: "REGISTRATION_TOKENS_INFO"
        });
    },

    loadUserFromStoredToken: (): IAppThunkAction => async (dispatch) => {
        const token = TokenProvider.GetStoredTokenParsed();
        // Sync with sales site in case cookie was cleared for some reason
        if (token) {
            AuthCookieProvider.SetCookie(token);
        }

        updatePosthogUserInfo(posthog);
        updateSentryUserInfo(Sentry);

        dispatch({ type: "LOAD_USER_INFO", token });
    },

    loadUserList: (): IAppThunkAction => async (dispatch) => {
        dispatch({
            type: "TOGGLE_USER_LIST_LOAD_STATE",
            usersLoadState: LoadState.Loading
        });

        const users = await AccountApi.GetUsers();
        dispatch({
            type: "UPDATE_USER_LIST",
            admins: users.administrators,
            bidders: users.bidders,
            usersLoadState: LoadState.Loaded
        });
    },

    loginUser:
        (email: string, password: string): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "TOGGLE_LOGON_LOAD_STATE",
                authLoadState: LoadState.Loading
            });

            // call API to attempt login
            const token = await AccountApi.Login(email, password);
            if (token) {
                await SignalRProvider.ResetAllConnections(
                    dispatch as IAppDispatch,
                    getState
                );
                const parsedToken = TokenProvider.ParseTokenString(token);

                if (parsedToken) {
                    AuthCookieProvider.SetCookie(parsedToken);
                }

                //Set this last or the "logged in" check will catch it and screw up the logic
                TokenProvider.SetStoredToken(token);

                updatePosthogUserInfo(posthog);
                updateSentryUserInfo(Sentry);

                dispatch({ type: "LOAD_USER_INFO", token: parsedToken });
            } else {
                dispatch({ type: "TOGGLE_LOGON_FAILURE", failure: true });
            }
        },

    logoutUser: (): IAppThunkAction => async (dispatch, getState) => {
        const parsedToken = getState().userStoreState.user?.token;
        if (parsedToken) {
            AuthCookieProvider.DeleteCookie();
        }

        TokenProvider.ClearStoredToken();
        await SignalRProvider.ResetAllConnections(
            dispatch as IAppDispatch,
            getState
        );

        posthog.reset();
        Sentry.setUser(null);
        dispatch({ type: "LOGOUT_USER" });

        window.location.href = new URL(
            window.location.origin + "/auctions"
        ).href;
    },

    registerUser:
        (newAcct: IRegisterAccount): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "TOGGLE_LOGON_LOAD_STATE",
                authLoadState: LoadState.Loading
            });

            // call API to register and get a token in return
            const result = await AccountApi.Register(newAcct);
            if (result.token) {
                await SignalRProvider.ResetAllConnections(
                    dispatch as IAppDispatch,
                    getState
                );

                const parsedToken = TokenProvider.ParseTokenString(
                    result.token
                );

                if (parsedToken) {
                    AuthCookieProvider.SetCookie(parsedToken);
                }
                TokenProvider.SetStoredToken(result.token);

                updatePosthogUserInfo(posthog);
                updateSentryUserInfo(Sentry);

                dispatch({ type: "LOAD_USER_INFO", token: parsedToken });
            } else {
                const errors = result.errors.map((e) => e.description);
                dispatch({ type: "TOGGLE_REG_FAILURE", failure: true, errors });
            }
        },

    toggleRedirectToLogin:
        (redirect: boolean): IAppThunkAction =>
        async (dispatch) => {
            dispatch({ type: "TOGGLE_REDIRECT_TO_LOGIN", redirect });
        },

    updateLocation:
        (location: string): IAppThunkAction =>
        (dispatch) =>
            dispatch({ type: "UPDATE_LOCATION", location }),

    resetPassword:
        (acct: IResetPassword): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "TOGGLE_LOGON_LOAD_STATE",
                authLoadState: LoadState.Loading
            });

            // call API to register and get a token in return
            const result = await AccountApi.ResetPassword(acct);
            if (result.token) {
                TokenProvider.SetStoredToken(result.token);
                await SignalRProvider.ResetAllConnections(
                    dispatch as IAppDispatch,
                    getState
                );

                const parsedToken = TokenProvider.GetStoredTokenParsed();

                updatePosthogUserInfo(posthog);
                updateSentryUserInfo(Sentry);

                dispatch({ type: "LOAD_USER_INFO", token: parsedToken });
            } else {
                const errors = result.errors.map((e) => e.description);
                dispatch({
                    type: "TOGGLE_RESET_PASSWORD_FAILURE",
                    failure: true,
                    errors
                });
            }
        },

    resetPasswordTokenCheck:
        (token: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_RESET_TOKEN_LOAD_STATE",
                resetPasswordTokenLoadState: LoadState.Loading
            });

            // call API to check reset token and ensure validity
            const result = await AccountApi.ResetPasswordTokenCheck(token);
            dispatch({
                type: "TOGGLE_RESET_TOKEN_LOAD_STATE",
                resetPasswordTokenLoadState: LoadState.Loaded
            });

            if (!result) {
                dispatch({
                    type: "TOGGLE_RESET_TOKEN_ERROR",
                    resetPasswordTokenError: true
                });
            }
        },

    toggleLogonFailure:
        (failure: boolean): IAppThunkAction =>
        (dispatch) =>
            dispatch({ type: "TOGGLE_LOGON_FAILURE", failure }),

    toggleRegFailure:
        (failure: boolean, errors: string[]): IAppThunkAction =>
        (dispatch) =>
            dispatch({ type: "TOGGLE_REG_FAILURE", failure, errors }),

    toggleResetCreateUser: (): IAppThunkAction => (dispatch) =>
        dispatch({ type: "TOGGLE_RESET_CREATE_USER" }),

    toggleResetDeleteUser: (): IAppThunkAction => (dispatch) =>
        dispatch({ type: "TOGGLE_RESET_DELETE_USER" }),

    updateUserProfile:
        (userProfile: IUserProfile): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "UPDATE_USER_PROFILE_LOAD_STATE",
                authLoadState: LoadState.Loading
            });

            // call API to register and get a token in return
            const result = await AccountApi.UpdateUserProfile(userProfile);
            if (result.token) {
                await SignalRProvider.ResetAllConnections(
                    dispatch as IAppDispatch,
                    getState
                );
                const parsedToken = TokenProvider.ParseTokenString(
                    result.token
                );

                if (parsedToken) {
                    AuthCookieProvider.SetCookie(parsedToken);
                }

                TokenProvider.SetStoredToken(result.token);

                updatePosthogUserInfo(posthog);
                updateSentryUserInfo(Sentry);

                dispatch({ type: "LOAD_USER_INFO", token: parsedToken });
                dispatch({ type: "UPDATE_USER_PROFILE" });
                dispatch({ type: "TOGGLE_RESET_UPDATE_USER_PROFILE" });
            } else {
                const errors = result.errors.map((e) => e.description);
                dispatch({
                    type: "UPDATE_USER_PROFILE_FAILURE",
                    failure: true,
                    errors
                });
            }
        },

    setPassword:
        (setPassword: ISetPassword): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "SET_PASSWORD_LOAD_STATE",
                setPasswordLoadState: LoadState.Loading
            });

            // call API to register and get a token in return
            const result = await AccountApi.SetPassword(setPassword);
            if (result.userId) {
                dispatch({ type: "SET_PASSWORD" });
                dispatch({ type: "TOGGLE_RESET_SET_PASSWORD" });
            } else {
                const errors = result.errors.map((e) => e.description);
                dispatch({
                    type: "SET_PASSWORD_FAILURE",
                    failure: true,
                    errors
                });
            }
        }
};

export const reducer: Reducer<IUserStoreState> = (
    state: IUserStoreState | undefined,
    incomingAction: AnyAction
) => {
    const action = incomingAction as KnownAction;
    const prevState = state || defaultState;

    switch (action.type) {
        case "UPDATE_USER_STATUS": {
            const bidders = prevState.bidders.map((bidder) =>
                bidder.email === action.email
                    ? Object.assign({}, bidder, { enabled: action.enable })
                    : bidder
            );
            return {
                ...prevState,
                bidders
            };
        }
        case "LOAD_USER_INFO": {
            if (!action.token) {
                return defaultAuthLoadedState;
            }

            const user = {
                email: action.token.sub,
                id: action.token.nameid,
                name: action.token.given_name,
                roles: action.token.roles,
                token: action.token
            } as IUserLogin;

            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                user
            };
        }
        case "LOGOUT_USER": {
            return defaultAuthLoadedState;
        }
        case "TOGGLE_LOGON_FAILURE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                logonFailure: action.failure
            };
        }
        case "TOGGLE_LOGON_LOAD_STATE": {
            return {
                ...prevState,
                authLoadState: action.authLoadState
            };
        }
        case "REGISTRATION_TOKENS_INFO_LOAD_STATE": {
            return {
                ...prevState,
                authLoadState: action.authLoadState
            };
        }
        case "REGISTRATION_TOKENS_INFO": {
            return {
                ...prevState,
                authLoadState: action.authLoadState,
                captchaKey: action.captchaKey,
                stripeKey: action.stripeKey
            };
        }
        case "FORGOT_PASSWORD_LOAD_STATE": {
            return {
                ...prevState,
                authLoadState: action.authLoadState
            };
        }
        case "FORGOT_PASSWORD": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                forgotPasswordSucceed: true
            };
        }
        case "FORGOT_PASSWORD_FAILURE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                forgotPasswordFailErrors: action.errors,
                forgotPasswordFailure: true
            };
        }
        case "TOGGLE_RESET_PASSWORD_FAILURE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                resetPasswordFailErrors: action.errors,
                resetPasswordFailure: true
            };
        }
        case "TOGGLE_RESET_TOKEN_ERROR": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded
            };
        }
        case "TOGGLE_RESET_TOKEN_LOAD_STATE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded
            };
        }
        case "TOGGLE_USER_LIST_LOAD_STATE": {
            return {
                ...prevState,
                usersLoadState: LoadState.Loading
            };
        }
        case "GET_USER_PROFILE_LOAD_STATE": {
            return {
                ...prevState,
                updateUserProfileFailErrors: [],
                updateUserProfileFailure: false,
                updateUserProfileSucceed: false,
                userProfile: null,
                userProfileLoadState: action.userProfileLoadState
            };
        }
        case "GET_USER_PROFILE": {
            return {
                ...prevState,
                userProfile: action.userProfile,
                userProfileLoadState: LoadState.Loaded
            };
        }
        case "TOGGLE_REG_FAILURE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                regFailErrors: action.errors,
                regFailure: action.failure
            };
        }
        case "UPDATE_USER_PROFILE_LOAD_STATE": {
            return {
                ...prevState,
                authLoadState: action.authLoadState
            };
        }
        case "UPDATE_USER_PROFILE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                updateUserProfileSucceed: true
            };
        }
        case "TOGGLE_RESET_UPDATE_USER_PROFILE": {
            return {
                ...prevState,
                updateUserProfileFailErrors: [],
                updateUserProfileFailure: false,
                updateUserProfileSucceed: false
            };
        }
        case "UPDATE_USER_LIST": {
            return {
                ...prevState,
                admins: action.admins,
                bidders: action.bidders,
                usersLoadState: action.usersLoadState
            };
        }
        case "UPDATE_USER_PROFILE_FAILURE": {
            return {
                ...prevState,
                authLoadState: LoadState.Loaded,
                updateUserProfileFailErrors: action.errors,
                updateUserProfileFailure: true,
                updateUserProfileSucceed: false
            };
        }
        case "CREATE_USER_LOAD_STATE": {
            return {
                ...prevState,
                createUserState: action.createUserState
            };
        }
        case "CREATE_USER": {
            return {
                ...prevState,
                createUserState: LoadState.NotLoaded,
                createUserSucceed: true
            };
        }
        case "CREATE_USER_FAILURE": {
            return {
                ...prevState,
                createUserFailErrors: action.errors,
                createUserFailure: action.failure,
                createUserState: LoadState.NotLoaded
            };
        }
        case "TOGGLE_RESET_CREATE_USER": {
            return {
                ...prevState,
                createUserFailErrors: [],
                createUserFailure: false,
                createUserState: LoadState.NotLoaded,
                createUserSucceed: false
            };
        }
        case "DELETE_USER_LOAD_STATE": {
            return {
                ...prevState,
                deleteUserState: action.deleteUserState
            };
        }
        case "TOGGLE_RESET_DELETE_USER": {
            return {
                ...prevState,
                deleteUserState: LoadState.NotLoaded,
                deleteUserSucceed: false
            };
        }
        case "DELETE_USER": {
            return {
                ...prevState,
                deleteUserState: LoadState.NotLoaded,
                deleteUserSucceed: true
            };
        }
        case "SET_PASSWORD_LOAD_STATE": {
            return {
                ...prevState,
                setPasswordLoadState: action.setPasswordLoadState
            };
        }
        case "SET_PASSWORD": {
            return {
                ...prevState,
                setPasswordLoadState: LoadState.NotLoaded,
                setPasswordSucceed: true
            };
        }
        case "SET_PASSWORD_FAILURE": {
            return {
                ...prevState,
                setPasswordFailErrors: action.errors,
                setPasswordFailure: action.failure,
                setPasswordLoadState: LoadState.NotLoaded
            };
        }
        case "TOGGLE_RESET_SET_PASSWORD": {
            return {
                ...prevState,

                setPasswordFailErrors: [],
                setPasswordFailure: false,
                setPasswordLoadState: LoadState.NotLoaded,
                setPasswordSucceed: false
            };
        }
        case "TOGGLE_REDIRECT_TO_LOGIN": {
            return {
                ...prevState,
                redirectToLogin: action.redirect
            };
        }
        case "UPDATE_LOCATION": {
            return {
                ...prevState,
                prevLocation: action.location
            };
        }
        default:
            break;
    }

    return prevState;
};

export const defaultState = {
    admins: [],
    authLoadState: LoadState.NotLoaded,
    bidders: [],
    captchaKey: "",
    createUserFailErrors: [],
    createUserFailure: false,
    createUserState: LoadState.NotLoaded,
    createUserSucceed: false,
    deleteUserState: LoadState.NotLoaded,
    deleteUserSucceed: false,
    forgotPasswordFailErrors: [],
    forgotPasswordFailure: false,
    forgotPasswordSucceed: false,
    logonFailure: false,
    prevLocation: "",
    redirectToLogin: false,
    regFailErrors: [],
    regFailure: false,
    resetPasswordFailErrors: [],
    resetPasswordFailure: false,
    resetPasswordTokenError: false,
    resetPasswordTokenLoadState: LoadState.NotLoaded,
    setPasswordFailErrors: [],
    setPasswordFailure: false,
    setPasswordLoadState: LoadState.NotLoaded,
    setPasswordSucceed: false,
    stripeKey: "",
    updateUserProfileFailErrors: [],
    updateUserProfileFailure: false,
    updateUserProfileSucceed: false,
    user: null,
    userProfile: null,
    userProfileLoadState: LoadState.NotLoaded,
    usersLoadState: LoadState.NotLoaded
} as IUserStoreState;

const defaultAuthLoadedState = {
    admins: [],
    authLoadState: LoadState.Loaded,
    bidders: [],
    captchaKey: "",
    createUserFailErrors: [],
    createUserFailure: false,
    createUserState: LoadState.NotLoaded,
    createUserSucceed: false,
    deleteUserState: LoadState.NotLoaded,
    deleteUserSucceed: false,
    forgotPasswordFailErrors: [],
    forgotPasswordFailure: false,
    forgotPasswordSucceed: false,
    logonFailure: false,
    prevLocation: "",
    redirectToLogin: false,
    regFailErrors: [],
    regFailure: false,
    resetPasswordFailErrors: [],
    resetPasswordFailure: false,
    resetPasswordTokenError: false,
    resetPasswordTokenLoadState: LoadState.NotLoaded,
    setPasswordFailErrors: [],
    setPasswordFailure: false,
    setPasswordLoadState: LoadState.NotLoaded,
    setPasswordSucceed: false,
    stripeKey: "",
    updateUserProfileFailErrors: [],
    updateUserProfileFailure: false,
    updateUserProfileSucceed: false,
    user: null,
    userProfile: null,
    userProfileLoadState: LoadState.NotLoaded,
    usersLoadState: LoadState.NotLoaded
} as IUserStoreState;
